import { supabase } from "@/lib/supabaseClient";

// Resize/compress large images (e.g. phone photos) before upload for faster OCR.
export async function compressImage(file, maxDim = 1600, quality = 0.82) {
  if (!file || !file.type?.startsWith("image/")) return file;
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = URL.createObjectURL(file);
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    if (scale >= 1 && file.size < 1.5 * 1024 * 1024) { URL.revokeObjectURL(img.src); return file; }
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(img.src);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
  } catch (_) { return file; }
}

function sanitize(name) {
  const base = (name.split("/").pop() || name).toLowerCase().replace(/\s+/g, "-");
  return base.replace(/[^a-z0-9\-_.]/g, "");
}

// Avatar → public 'avatars' bucket at <user_id>/<file>. Persists profiles.avatar_url.
export async function uploadAvatar(file) {
  const { data: { user }, error: uErr } = await supabase.auth.getUser();
  if (uErr || !user) throw new Error("Ingen inloggad användare");
  file = await compressImage(file, 512, 0.85);
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const path = `${user.id}/avatar-${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from("avatars").upload(path, file, {
    upsert: true, contentType: file.type || "image/jpeg", cacheControl: "3600",
  });
  if (error) throw new Error(error.message);
  const { data } = supabase.storage.from("avatars").getPublicUrl(path);
  const url = data.publicUrl;
  await supabase.from("profiles").update({ avatar_url: url }).eq("id", user.id);
  return url;
}

// Attachment (e.g. invoice photo) → private 'attachments' bucket at <restaurant_id>/<type>/<file>.
export async function uploadAttachment({ restaurantId, entityType, file }) {
  const path = `${restaurantId}/${entityType}/${Date.now()}-${sanitize(file.name)}`;
  const { error } = await supabase.storage.from("attachments").upload(path, file, {
    upsert: false, contentType: file.type || "image/jpeg", cacheControl: "3600",
  });
  if (error) throw new Error(error.message);
  return { bucket: "attachments", path };
}

export async function getAttachmentUrl(path, expiresIn = 600) {
  const { data, error } = await supabase.storage.from("attachments").createSignedUrl(path, expiresIn);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}
